/* eslint-disable @typescript-eslint/no-explicit-any */
import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import { format } from 'date-fns'

import Image from '~/components/Helpers/Image'
import Tag, { TagProps } from '~/components/UI/General/Tag/Tag'
import { CardProps } from '~/components/types/card'

const cardVariants = cva(
  '@container/NewsCard relative flex max-h-[500px] !h-[500px] w-full flex-col sm:basis-[calc(50%-12px)] lg:grow lg:basis-0 unstyled-link group',
  {
    variants: {
      backgroundColour: {
        'ink-blue': 'bg-blue-d text-white [&_a]:hover:text-white',
        'mid-blue': 'bg-blue-m text-white [&_a]:hover:text-white',
        'light-grey':
          'bg-grey-l text-blue-d [&_a]:text-blue-d [&_a]:hover:text-blue-d',
        white: 'bg-white text-blue-d [&_a]:text-blue-d [&_a]:hover:text-blue-d'
      }
    },
    defaultVariants: {
      backgroundColour: 'ink-blue'
    }
  }
)

export type NewsCardProps = CardProps & {
  content: {
    heading: string
    description: string
    date?: string | { from: string; to: string }
    primaryCategory?: TagProps
    categories: TagProps[]
    sys: { uri: string; contentTypeId?: string }
    entryTitle: string
    showDescription?: boolean
    seoStandard?: {
      title: string
    }
    status?: {
      entryTitle: string
    }
    heroStandardAvantGarde?: Array<{
      image: any
    }>
    showFeatured?: boolean
  }
}

const NewsCard = ({ content, carousel, className }: NewsCardProps) => {
  const caseStudy: any = content.sys?.contentTypeId ===
    'pageCaseStudyResearch' && {
    heading: content.entryTitle,
    description: content.description,
    categories: content.categories || [
      {
        data: {
          taxonomy: content.status,
          label: content.status?.entryTitle,
          backgroundColour:
            content.status?.entryTitle === 'Complete'
              ? 'light-aqua'
              : 'light-orange',
          showIcon: false
        }
      }
    ],
    sys: {
      contentTypeId: 'pageCaseStudyResearch',
      uri: content.sys.uri
    },
    featured: content.showFeatured || false,
    entryTitle: content.seoStandard?.title,
    image:
      content.image ||
      (content.heroStandardAvantGarde &&
        content.heroStandardAvantGarde[0].image),
    backgroundColour: content.backgroundColour || 'light-grey',
    showDescription: true
  }

  const {
    backgroundColour,
    heading,
    date,
    description,
    image,
    primaryCategory,
    categories,
    featured,
    sys,
    entryTitle,
    showDescription
  } = caseStudy || content

  const formattedDate = date ? format(date, 'dd MMMM yyyy') : null

  const category = primaryCategory || categories[0]

  const foregroundColour =
    backgroundColour === 'ink-blue' || backgroundColour === 'mid-blue'
      ? 'white'
      : 'ink-blue'

  return (
    <a
      href={sys.uri}
      className={cn(
        cardVariants({ backgroundColour }),
        `${carousel && featured && 'lg:flex-row'}`,
        className
      )}
      aria-label={entryTitle}
    >
      {image && (
        <div
          className={`${carousel && featured ? 'h-1/2 lg:h-full lg:min-w-1/2' : 'h-1/2'}`}
        >
          <Image image={image} classes=' h-full w-full object-cover' />
        </div>
      )}
      <div
        className={cn(
          'p-xs-plus sm:p-s lg:p-m flex grow flex-col max-sm:justify-end',
          !carousel || featured ? 'gap-xs sm:gap-s' : ''
        )}
      >
        {category && (
          <div className='gap-s flex flex-wrap'>
            <Tag
              className={cn(
                '[&>img]:size-2xs-plus !text-[10px] [&>span]:line-clamp-1',
                carousel && !featured && 'top-xs right-xs absolute max-w-3/4'
              )}
              data={{
                ...category.data,
                taxonomy: category,
                backgroundColour:
                  category?.data?.backgroundColour ||
                  (carousel && !featured ? 'light-pink' : 'none'),
                icon: {
                  asset: {
                    sys: {
                      uri: `/site-elements/icons/single-colour/${carousel && !featured ? 'ink-blue' : foregroundColour}/tag.svg`
                    }
                  }
                }
              }}
            />
          </div>
        )}

        <div className='shadow-none group-hover:underline'>
          <h4
            className={cn(
              'mb-xs max-w-fit',
              image ? 'lg:line-clamp-1' : '',
              carousel && !featured ? 'lg:text-base-lg' : '',
              (carousel && featured && !image) || (!carousel && !image)
                ? 'lg:text-h3-lg'
                : ''
            )}
          >
            {heading}
          </h4>
        </div>
        {(showDescription || !image || (carousel && featured)) && (
          <p
            className={cn(
              'hidden lg:line-clamp-3',
              carousel && image ? '' : 'flex'
              // `${!formattedDate ? 'grow' : ''}`
            )}
          >
            {description}
          </p>
        )}
        {formattedDate && (
          <div className='flex grow items-end'>
            <p className='tag grow'>{formattedDate}</p>
          </div>
        )}
      </div>
    </a>
  )
}

export default NewsCard
